"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useToast } from "@/contexts/ToastContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { useWebPushSubscription } from "@/lib/hooks/useWebPushSubscription";
import { supabase } from "@/lib/supabase";
import { isGrowthPhaseNotificationDefaultsEnabled } from "@/lib/notifications/growth-phase-notification-defaults";
import {
  applyHomepageNearMeQuickOptIn,
  phoneDigitsFromProfilePhone,
  profileLocationUsableForNearMe,
} from "@/lib/notifications/homepage-near-me-quick-opt-in";
import {
  evaluateNotificationSetupEligibility,
  getNotificationSetupBannerCopy,
  type NotificationPrefsSnapshot,
} from "@/lib/notifications/notification-setup-eligibility";
import { NOTIFICATION_SETUP_BANNER_STATE_EVENT } from "@/lib/notifications/notification-setup-banner-events";
import {
  clearNotificationSetupBannerDismissState,
  markNotificationSetupBannerSessionShown,
  readNotificationSetupBannerDismissCount,
  readNotificationSetupBannerDismissedAt,
  recordNotificationSetupBannerDismissed,
} from "@/lib/notifications/notification-setup-banner-policy";
import {
  readPostRegistrationSetupGraceActive,
  shouldDeferNotificationSetupSheet,
  shouldSuppressNotificationSetupBanner,
} from "@/lib/onboardingEngine/notification-setup-coordination";
import { isPwaPostInstallPushPending } from "@/components/PwaPostInstallPushPrompt";
import { ensurePushServiceWorkerRegistered } from "@/lib/push-notifications/ensure-push-service-worker";
import {
  recordNotificationSetupBannerCtaClick,
  recordNotificationSetupBannerDismissed as recordSetupBannerDismissedMetric,
  recordNotificationSetupBannerShown,
  recordNotificationSetupGapResolved,
  recordNotificationSetupSheetCompleted,
} from "@/lib/notifications/notification-setup-metrics-client";

async function fetchNotificationPrefsSnapshot(
  userId: string,
): Promise<NotificationPrefsSnapshot | null> {
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("notify_new_listings_near_me")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("notification_preferences load (setup banner):", error);
    return null;
  }

  if (!data) return null;

  return {
    notify_new_listings_near_me: data.notify_new_listings_near_me ?? null,
  };
}

async function readBrowserPushSubscribed(): Promise<boolean> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }
  try {
    const reg = await ensurePushServiceWorkerRegistered();
    return Boolean(await reg.pushManager.getSubscription());
  } catch {
    return false;
  }
}

function readBrowserPushPermission(): NotificationPermission | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
}

function resolveSetupComplete(input: {
  nearMePlatformEnabled: boolean;
  growthDefaultsOn: boolean;
  prefs: NotificationPrefsSnapshot | null;
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  pushSupported: boolean;
  pushVapidConfigured: boolean;
  pushPermission: NotificationPermission | "unsupported";
  browserPushSubscribed: boolean;
}): boolean {
  const gaps = evaluateNotificationSetupEligibility({
    user: input.user,
    authLoading: false,
    registrationOnboardingComplete: true,
    nearMePlatformEnabled: input.nearMePlatformEnabled,
    growthDefaultsOn: input.growthDefaultsOn,
    prefs: input.prefs,
    prefsLoaded: true,
    pushSupported: input.pushSupported,
    pushVapidConfigured: input.pushVapidConfigured,
    pushPermission: input.pushPermission,
    browserPushSubscribed: input.browserPushSubscribed,
    pathname: "/market",
    profileTab: null,
    dismissedAtIso: null,
    dismissCount: 0,
  }).gaps;

  return !gaps.nearMeGap && !gaps.pushGap;
}

export function useNotificationSetupBanner() {
  const pathname = usePathname() ?? "/";
  // Hydrate-safe: Next `useSearchParams` suspends and forced a layout-wide
  // `<Suspense fallback={null}>` around Header/main → streaming hole + #418.
  const profileTab = useQueryParam("tab");
  const { user, isLoading: authLoading } = useAuth();
  const { activePromptId } = useOnboarding();
  const { showToast } = useToast();
  const {
    newListingNearMeNotificationsEnabled,
    nearMeUserDefaultsPolicy,
    isLoading: settingsLoading,
  } = useSystemSettings();
  const webPush = useWebPushSubscription();

  const [prefs, setPrefs] = useState<NotificationPrefsSnapshot | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [dismissCount, setDismissCount] = useState(() =>
    readNotificationSetupBannerDismissCount(),
  );
  const [dismissedAtIso, setDismissedAtIso] = useState<string | null>(() =>
    readNotificationSetupBannerDismissedAt(),
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [nearMeBusy, setNearMeBusy] = useState(false);
  const [finishBusy, setFinishBusy] = useState(false);
  const bannerShownLogged = useRef(false);

  const growthDefaultsOn = isGrowthPhaseNotificationDefaultsEnabled();
  const registrationOnboardingComplete = Boolean(
    user?.registrationOnboardingCompletedAt,
  );

  const locationOk = user
    ? profileLocationUsableForNearMe({
        location: user.location,
        location_state_id: user.location_state_id,
        location_lga_id: user.location_lga_id,
        location_neighborhood_id: user.location_neighborhood_id,
      })
    : false;

  const refreshPrefs = useCallback(async (userId: string) => {
    const next = await fetchNotificationPrefsSnapshot(userId);
    setPrefs(next);
    setPrefsLoaded(true);
    return next;
  }, []);

  useEffect(() => {
    if (!user?.id) {
      const timer = setTimeout(() => {
        setPrefs(null);
        setPrefsLoaded(true);
      }, 0);
      return () => clearTimeout(timer);
    }

    let cancelled = false;
    const userId = user.id;
    const timer = setTimeout(() => {
      setPrefsLoaded(false);
      void fetchNotificationPrefsSnapshot(userId).then((next) => {
        if (cancelled) return;
        setPrefs(next);
        setPrefsLoaded(true);
      });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user]);

  useEffect(() => {
    const refreshDismissState = () => {
      setDismissCount(readNotificationSetupBannerDismissCount());
      setDismissedAtIso(readNotificationSetupBannerDismissedAt());
      bannerShownLogged.current = false;
    };

    window.addEventListener(
      NOTIFICATION_SETUP_BANNER_STATE_EVENT,
      refreshDismissState,
    );
    return () => {
      window.removeEventListener(
        NOTIFICATION_SETUP_BANNER_STATE_EVENT,
        refreshDismissState,
      );
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const onFocus = () => {
      void refreshPrefs(user.id);
      void webPush.refreshBrowserSubscription();
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshPrefs, user?.id, webPush]);

  const eligibility = useMemo(() => {
    const dataReady = !settingsLoading && prefsLoaded;

    return evaluateNotificationSetupEligibility({
      user: user ?? null,
      authLoading: authLoading || settingsLoading || !dataReady,
      registrationOnboardingComplete,
      nearMePlatformEnabled: newListingNearMeNotificationsEnabled,
      growthDefaultsOn,
      prefs,
      prefsLoaded: dataReady,
      pushSupported: webPush.isSupported,
      pushVapidConfigured: webPush.vapidConfigured,
      pushPermission: webPush.permission,
      browserPushSubscribed: webPush.browserSubscribed,
      pathname,
      profileTab,
      dismissedAtIso,
      dismissCount,
    });
  }, [
    authLoading,
    dismissCount,
    dismissedAtIso,
    growthDefaultsOn,
    newListingNearMeNotificationsEnabled,
    pathname,
    prefs,
    prefsLoaded,
    profileTab,
    registrationOnboardingComplete,
    settingsLoading,
    user,
    webPush.browserSubscribed,
    webPush.isSupported,
    webPush.permission,
    webPush.vapidConfigured,
  ]);

  const copy = useMemo(
    () =>
      getNotificationSetupBannerCopy(
        eligibility.mode,
        eligibility.gaps.nearMeGapReason,
      ),
    [eligibility.gaps.nearMeGapReason, eligibility.mode],
  );

  useEffect(() => {
    if (
      !eligibility.visible ||
      bannerShownLogged.current ||
      !eligibility.mode
    ) {
      return;
    }
    bannerShownLogged.current = true;
    markNotificationSetupBannerSessionShown();
    recordNotificationSetupBannerShown(eligibility.mode);
  }, [eligibility.mode, eligibility.visible]);

  const clearSnoozeIfComplete = useCallback(
    async (
      nextPrefs: NotificationPrefsSnapshot | null,
      browserPushSubscribed: boolean,
      pushPermission: NotificationPermission | "unsupported",
    ) => {
      if (!user?.id) return;
      const complete = resolveSetupComplete({
        nearMePlatformEnabled: newListingNearMeNotificationsEnabled,
        growthDefaultsOn,
        prefs: nextPrefs,
        user,
        pushSupported: webPush.isSupported,
        pushVapidConfigured: webPush.vapidConfigured,
        pushPermission,
        browserPushSubscribed,
      });
      if (complete) {
        clearNotificationSetupBannerDismissState();
        setDismissCount(0);
        setDismissedAtIso(null);
      }
    },
    [
      growthDefaultsOn,
      newListingNearMeNotificationsEnabled,
      user,
      webPush.isSupported,
      webPush.vapidConfigured,
    ],
  );

  const enableNearMe = useCallback(async (): Promise<boolean> => {
    if (!user?.id || !locationOk || !eligibility.gaps.nearMeGap) {
      return false;
    }

    setNearMeBusy(true);
    try {
      const nowIso = new Date().toISOString();
      const result = await applyHomepageNearMeQuickOptIn(supabase, {
        userId: user.id,
        nowIso,
        notifyWhatsapp: false,
        phoneDigits: phoneDigitsFromProfilePhone(user.phone ?? null),
        previousWhatsapp: {
          enabled: false,
          opt_in_at: null,
          opt_out_at: null,
        },
        nearMePolicy: nearMeUserDefaultsPolicy,
      });

      if (!result.ok) {
        showToast("Could not save near-me alerts. Please try again.", "error");
        return false;
      }

      const nextPrefs: NotificationPrefsSnapshot = {
        notify_new_listings_near_me: true,
      };
      setPrefs(nextPrefs);
      recordNotificationSetupGapResolved("near_me");
      const pushPermission = readBrowserPushPermission();
      const browserPushSubscribed = await readBrowserPushSubscribed();
      await clearSnoozeIfComplete(
        nextPrefs,
        browserPushSubscribed,
        pushPermission,
      );
      showToast("Near-me alerts are on.", "success");
      return true;
    } finally {
      setNearMeBusy(false);
    }
  }, [
    clearSnoozeIfComplete,
    eligibility.gaps.nearMeGap,
    locationOk,
    nearMeUserDefaultsPolicy,
    showToast,
    user,
  ]);

  const enablePush = useCallback(async (): Promise<boolean> => {
    if (!eligibility.gaps.pushGap) return false;

    await webPush.enablePush({ source: "setup_banner" });
    await webPush.refreshBrowserSubscription();

    const pushPermission = readBrowserPushPermission();
    const browserPushSubscribed = await readBrowserPushSubscribed();

    if (pushPermission === "granted" && browserPushSubscribed) {
      recordNotificationSetupGapResolved("push");
      await clearSnoozeIfComplete(prefs, browserPushSubscribed, pushPermission);
      showToast("Notifications enabled on this device.", "success");
      return true;
    }

    if (webPush.lastError) {
      showToast(webPush.lastError, "error");
    }
    return false;
  }, [
    clearSnoozeIfComplete,
    eligibility.gaps.pushGap,
    prefs,
    showToast,
    webPush,
  ]);

  const finishSetup = useCallback(async () => {
    if (!user?.id) return;

    setFinishBusy(true);
    const resolvedNearMe = !eligibility.gaps.nearMeGap;
    const resolvedPush = !eligibility.gaps.pushGap;
    let nearMeDone = resolvedNearMe;
    let pushDone = resolvedPush;

    try {
      if (eligibility.gaps.nearMeGap && locationOk) {
        nearMeDone = await enableNearMe();
      }
      if (eligibility.gaps.pushGap) {
        pushDone = await enablePush();
      }

      const latestPrefs = await refreshPrefs(user.id);
      await webPush.refreshBrowserSubscription();
      const pushPermission = readBrowserPushPermission();
      const browserPushSubscribed = await readBrowserPushSubscribed();

      const complete = resolveSetupComplete({
        nearMePlatformEnabled: newListingNearMeNotificationsEnabled,
        growthDefaultsOn,
        prefs: nearMeDone ? { notify_new_listings_near_me: true } : latestPrefs,
        user,
        pushSupported: webPush.isSupported,
        pushVapidConfigured: webPush.vapidConfigured,
        pushPermission,
        browserPushSubscribed,
      });

      if (complete) {
        recordNotificationSetupSheetCompleted({
          near_me: nearMeDone,
          push: pushDone,
        });
        clearNotificationSetupBannerDismissState();
        setDismissCount(0);
        setDismissedAtIso(null);
        setSheetOpen(false);
        showToast(
          "You're all set — we'll keep you posted when it matters.",
          "success",
        );
      } else if (nearMeDone || pushDone) {
        showToast("Progress saved. Finish any remaining steps above.", "info");
      }
    } finally {
      setFinishBusy(false);
    }
  }, [
    eligibility.gaps.nearMeGap,
    eligibility.gaps.pushGap,
    enableNearMe,
    enablePush,
    growthDefaultsOn,
    locationOk,
    newListingNearMeNotificationsEnabled,
    refreshPrefs,
    showToast,
    user,
    webPush,
  ]);

  const dismiss = useCallback(() => {
    const next = recordNotificationSetupBannerDismissed();
    setDismissCount(next.dismissCount);
    setDismissedAtIso(next.dismissedAtIso);
    if (eligibility.mode) {
      recordSetupBannerDismissedMetric(eligibility.mode, next.dismissCount);
    }
  }, [eligibility.mode]);

  const openSheet = useCallback(() => {
    if (shouldDeferNotificationSetupSheet(activePromptId)) {
      showToast(
        "Finish the current tour first, then you can finish alert setup.",
        "info",
      );
      return;
    }
    if (eligibility.mode) {
      recordNotificationSetupBannerCtaClick(eligibility.mode, "sheet");
    }
    setSheetOpen(true);
  }, [activePromptId, eligibility.mode, showToast]);

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
  }, []);

  const bannerSuppressed = useMemo(
    () =>
      shouldSuppressNotificationSetupBanner({
        activeOnboardingPromptId: activePromptId,
        pwaPushPending: isPwaPostInstallPushPending(),
        postRegistrationGrace: readPostRegistrationSetupGraceActive(),
      }),
    [activePromptId],
  );

  return {
    visible: eligibility.visible && !bannerSuppressed,
    passiveChipVisible: eligibility.passiveChipVisible && !bannerSuppressed,
    passiveOnly: eligibility.policy.passiveOnly,
    copy,
    profileHref: eligibility.profileHref,
    mode: eligibility.mode,
    gaps: eligibility.gaps,
    dismiss,
    sheetOpen,
    openSheet,
    closeSheet,
    locationOk,
    nearMeBusy,
    finishBusy,
    pushBusy: webPush.busy,
    pushSupported: webPush.isSupported,
    pushVapidConfigured: webPush.vapidConfigured,
    pushPermission: webPush.permission,
    pushLastError: webPush.lastError,
    enableNearMe,
    enablePush,
    finishSetup,
  };
}
